import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, X, Send, Bot, User } from "lucide-react";

const SUGGESTIONS = [
  "What's low or out of stock at Derek's?",
  "Which raw materials need reordering?",
  "How much Cordyceps do we have and where?",
  "What is our most valuable finished product in stock?",
];

export default function AssistantWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setLoading(true);
    try {
      const { data } = await api.post("/assistant/chat", { message: q, session_id: sessionRef.current });
      sessionRef.current = data.session_id;
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", text: "Sorry — I couldn't reach the inventory data just now. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button data-testid="assistant-toggle" onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-stone-900 text-white shadow-lg shadow-stone-900/30 flex items-center justify-center hover:bg-stone-800 hover:scale-105 active:scale-95 transition-all">
        {open ? <X className="h-6 w-6" /> : <Sparkles className="h-6 w-6 text-amber-300" />}
      </button>

      {open && (
        <div data-testid="assistant-panel" className="fixed bottom-24 right-6 z-50 w-[92vw] max-w-md h-[560px] max-h-[75vh] rounded-2xl bg-white border border-stone-200 shadow-2xl shadow-stone-900/20 flex flex-col overflow-hidden animate-fade-up">
          <div className="px-4 py-3 border-b border-stone-100 bg-stone-900 text-white flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-lg bg-amber-700/90 flex items-center justify-center"><Bot className="h-4.5 w-4.5 text-amber-50" /></div>
            <div className="leading-tight">
              <div className="text-sm font-semibold">Inventory Assistant</div>
              <div className="text-[11px] text-stone-400">Ask about stock, reorders & facilities</div>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#FAFAF8]">
            {messages.length === 0 && (
              <div className="space-y-3">
                <p className="text-sm text-stone-500">Hi! I can answer questions about your live inventory. Try one of these:</p>
                <div className="space-y-2">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} data-testid="assistant-suggestion" onClick={() => send(s)}
                      className="block w-full text-left text-sm px-3 py-2 rounded-lg bg-white border border-stone-200 hover:border-stone-400 hover:bg-stone-50 transition-colors text-stone-700">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-2.5 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`assistant-msg-${m.role}`}>
                <div className={`h-7 w-7 rounded-full shrink-0 flex items-center justify-center ${m.role === "user" ? "bg-stone-200 text-stone-600" : "bg-amber-100 text-amber-800"}`}>
                  {m.role === "user" ? <User className="h-3.5 w-3.5" /> : <Bot className="h-3.5 w-3.5" />}
                </div>
                <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-stone-900 text-white rounded-tr-sm" : "bg-white border border-stone-200 text-stone-800 rounded-tl-sm"}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex gap-2.5" data-testid="assistant-loading">
                <div className="h-7 w-7 rounded-full bg-amber-100 text-amber-800 flex items-center justify-center"><Bot className="h-3.5 w-3.5" /></div>
                <div className="bg-white border border-stone-200 rounded-2xl rounded-tl-sm px-4 py-3 flex gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-stone-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="h-1.5 w-1.5 rounded-full bg-stone-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="h-1.5 w-1.5 rounded-full bg-stone-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
          </div>

          <div className="p-3 border-t border-stone-100 bg-white flex items-center gap-2">
            <Input data-testid="assistant-input" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask about your inventory…" className="flex-1 h-10" disabled={loading} />
            <Button data-testid="assistant-send" onClick={() => send()} disabled={loading || !input.trim()} className="h-10 w-10 p-0 bg-stone-900 hover:bg-stone-800 shrink-0"><Send className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
    </>
  );
}
